import React from 'react';
import { BrandPageFieldsFragment } from '@/types';
import { BrandStorefrontModule } from '@/utils/brand-page';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { shadow } from '@/styles';
import { useSmallLayoutSizeValue } from '@/contexts/layout-context';
import { View } from 'react-native';

interface Props {
  brandPageContent: BrandPageFieldsFragment;
}

const SMALL_IMAGE_HEIGHT = 270;
const SMALL_LOGO_SIZE = 104;
const LARGE_IMAGE_HEIGHT = 344;
const LARGE_LOGO_SIZE = 120;

export const BrandPageImageHeader: React.FC<Props> = ({ brandPageContent }) => {
  const [styles] = useStyles();
  const storefrontModule: BrandStorefrontModule | null =
    brandPageContent?.storefront_module;
  const {
    containerStyle,
    logoImageContainerStyle,
    logoImageStyle,
    mainImageStyle,
    mainImageURL,
  } = useSmallLayoutSizeValue(
    {
      containerStyle: styles.containerSmall,
      logoImageContainerStyle: styles.logoImageContainerSmall,
      logoImageStyle: styles.logoImageSmall,
      mainImageStyle: styles.mainImageSmall,
      mainImageURL: storefrontModule?.mobile_image_url,
    },
    {
      containerStyle: null,
      logoImageContainerStyle: styles.logoImageContainerLarge,
      logoImageStyle: styles.logoImageLarge,
      mainImageStyle: styles.mainImageLarge,
      mainImageURL: storefrontModule?.desktop_image_url,
    },
  );

  return mainImageURL && brandPageContent.logo_image_url ? (
    <View style={containerStyle}>
      <CachedImage
        alt=""
        source={{ uri: mainImageURL }}
        style={mainImageStyle}
        testID="brand-page-header-main-image"
      />
      <View style={[styles.logoImageContainer, logoImageContainerStyle]}>
        <CachedImage
          alt={brandPageContent.brand_name ?? ''}
          contentFit="contain"
          source={{ uri: brandPageContent.logo_image_url }}
          style={[styles.logoImage, logoImageStyle]}
          testID="brand-page-header-logo-image"
        />
      </View>
    </View>
  ) : null;
};

const useStyles = createStyles((theme) => ({
  containerSmall: {
    height: SMALL_IMAGE_HEIGHT + SMALL_LOGO_SIZE / 2,
  },
  logoImage: {
    backgroundColor: theme.colors.white,
  },
  logoImageContainer: {
    ...shadow({
      color: theme.colors.black,
      elevation: 5,
      opacity: 0.1,
      radius: 4,
      x: 0,
      y: 4,
    }),
    position: 'absolute',
  },
  logoImageContainerLarge: {
    left: 16,
    top: 16,
  },
  logoImageContainerSmall: {
    borderRadius: 16,
    bottom: 0,
    left: 16,
  },
  logoImageLarge: {
    borderRadius: 8,
    height: LARGE_LOGO_SIZE,
    width: LARGE_LOGO_SIZE,
  },
  logoImageSmall: {
    borderRadius: 16,
    height: SMALL_LOGO_SIZE,
    width: SMALL_LOGO_SIZE,
  },
  mainImageLarge: {
    height: LARGE_IMAGE_HEIGHT,
  },
  mainImageSmall: {
    height: SMALL_IMAGE_HEIGHT,
  },
}));
